import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { customAxiosInstance } from '../axios/axiosInstance';
import { AxiosResponse } from 'axios';
import { IError } from '../interfaces/IError';
import {
  GetSupplierForecastResponse,
  GetSupplierForecastSortTypeEnum,
  SupplierForecastApi,
  ErrorResponse,
  GetSupplierForecastResponseAllOfResult,
  BulkPostSupplierForecastRequest,
  BulkSupplierForecastResponseInfo,
  PutSupplierForecastResponsibleUserIdRequest,
  GetExportDataResponseAllOfResult,
  BulkPostSupplierForecastByCustomerForecastRequest,
  SupplierForecastFilterItem,
} from '../axios/generated/supplierForecastManage';
import { ISupplierForecastRepository } from '../interfaces/ISupplierForecastRepository';
import { axiosErrorHandler } from '../axios/axiosError';

/**
 * 仕入先内示APIの実装クラス
 */
export class SupplierForecastRepository implements ISupplierForecastRepository {
  /**
   * 仕入先内示情報テーブルから情報の取得を行う
   * @param  sortType - ソートの種類（asc/desc）
   * @param limit - 情報の表示最大数
   * @param offset - 表示する情報のオフセット
   * @param createdYear - 仕入先内示作成年
   * @param createdMonth - 仕入先内示作成月
   * @param sortTarget - どこの列をソート対象にするのかを指定するパラメータ
   * @param filterItem - フィルター対象の項目と情報
   * @param unassignedResponsibleUserFlag - 担当者未割当の情報を取得するかどうか
   */
  async getAll(
    sortType: GetSupplierForecastSortTypeEnum,
    limit: number,
    offset: number,
    createdYear: number,
    createdMonth: number,
    sortTarget?: string,
    filterItem?: SupplierForecastFilterItem,
    unassignedResponsibleUserFlag?: boolean
  ): Promise<INetworkResult<GetSupplierForecastResponseAllOfResult, IError>> {
    // GetCustomerForecastResponseAllOfResult
    const axiosInstance = await customAxiosInstance('dbAccess');
    const supplierForecastApi = new SupplierForecastApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<GetSupplierForecastResponse, ErrorResponse>;

    try {
      apiResponse = await supplierForecastApi.getSupplierForecast(
        sortType,
        limit,
        offset,
        createdYear,
        createdMonth,
        sortTarget,
        filterItem
      );
      switch (apiResponse.status) {
        case 200:
          const result: GetSupplierForecastResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 仕入先内示の一括登録
   * @param supplierForecastInfos - 仕入先内示情報の配列
   */
  async bulkPut(supplierForecastInfos: BulkPostSupplierForecastRequest): Promise<INetworkResult<BulkSupplierForecastResponseInfo, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const supplierForecastApi = new SupplierForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await supplierForecastApi.bulkPostSupplierForecast(supplierForecastInfos);
      switch (apiResponse.status) {
        case 200:
          const result: BulkSupplierForecastResponseInfo = apiResponse.data['result'];
          // return { status: { type: 'success', result: orderId } };
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 仕入先内示の担当者IDの更新
   * @param putInfo - 更新情報
   */
  async updateResponsibleUserId(putInfo: PutSupplierForecastResponsibleUserIdRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const supplierForecastApi = new SupplierForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await supplierForecastApi.putSupplierForecastResponsibleUserId(putInfo);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 仕入先内示の出力用のデータの一括取得
   * @param targetStartYear - 対象開始年
   * @param targetStartMonth - 対象開始月
   * @param targetEndYear - 対象終了年
   * @param targetEndMonth - 対象終了月
   * @param filterItem - フィルター対象の項目と情報
   */
  async bulkGetExportData(
    targetStartYear: number,
    targetStartMonth: number,
    targetEndYear: number,
    targetEndMonth: number,
    filterItem?: SupplierForecastFilterItem
  ): Promise<INetworkResult<GetExportDataResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const supplierForecastApi = new SupplierForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await supplierForecastApi.bulkGetSupplierForecastExportData(
        targetStartYear,
        targetStartMonth,
        targetEndYear,
        targetEndMonth,
        filterItem
      );
      switch (apiResponse.status) {
        case 200:
          const result: GetExportDataResponseAllOfResult = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 仕入先内示の一括登録（客先内示から）
   * @param supplierForecastInfo - 仕入先内示情報
   */
  async bulkPostByCustomerForecast(
    supplierForecastInfo: BulkPostSupplierForecastByCustomerForecastRequest
  ): Promise<INetworkResult<BulkSupplierForecastResponseInfo, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const supplierForecastApi = new SupplierForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await supplierForecastApi.bulkPostSupplierForecastByCustomerForecast(supplierForecastInfo);
      switch (apiResponse.status) {
        case 200:
          const result: BulkSupplierForecastResponseInfo = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
